/**
 * Staff panels for the submission detail page: carrier quotes, send-proposal,
 * payment/bind, and AI document extraction. Each takes the submission data and a
 * `patch` callback that merges a partial into `data` (optionally changing status).
 */
import {
  CARRIER_LIST,
  CARRIER_STATUS_LABEL,
  EXTRACT_FIELDS,
  GATEKEEPER_CARRIER_OPTIONS,
  GK_STATUS_LABEL,
  LOB_NAME,
  type GatekeeperCarrier,
  type Quote,
  type SubmissionData,
} from '@commpass/shared';
import { useMemo, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useCarriers } from '../data/reference';
import { currencyInput } from '../lib/format';
import { supabase } from '../lib/supabase';
import { SparkIcon } from './Icons';
import { useToast } from './Toast';

type Patch = (partial: Partial<SubmissionData>, status?: string) => Promise<void>;

function money(v: unknown): string {
  const n = Number(String(v ?? '').replace(/[^0-9.]/g, ''));
  if (!v || Number.isNaN(n) || n === 0) return String(v ?? '');
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function lobName(k: string): string {
  return k === 'All' ? 'All lines' : LOB_NAME[k] ?? k;
}

// AI-readable mime from a stored file's name (the extract endpoint accepts PDF/images only).
function docMime(fileName?: string): string | null {
  const ext = (fileName || '').toLowerCase().split('.').pop() || '';
  if (ext === 'pdf') return 'application/pdf';
  if (ext === 'png') return 'image/png';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'webp') return 'image/webp';
  return null;
}
// Chunked base64 so multi-MB files don't blow the call stack.
function toBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

// ---- Carrier quotes + proposal builder ----
export function QuotesPanel({ data, patch, id }: { data: SubmissionData; patch: Patch; id: string }) {
  const toast = useToast();
  const quotes = data.quotes ?? [];
  const lines = (data.lines ?? []) as string[];
  const lobKeys = lines.length ? lines : ['All'];

  // Built-in carriers + staff-added carriers (from Settings), deduped and A–Z.
  const { data: dbCarriers } = useCarriers();
  const carrierNames = useMemo(() => {
    const names = new Set<string>();
    for (const c of CARRIER_LIST) names.add(c.name);
    for (const c of dbCarriers ?? []) if (c.name) names.add(c.name);
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [dbCarriers]);

  const [carrier, setCarrier] = useState('');
  const [status, setStatus] = useState('quoted');
  const [premiums, setPremiums] = useState<Record<string, string>>({});
  const [payment, setPayment] = useState('');
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [building, setBuilding] = useState(false);
  // null = the form is adding a new carrier; a number = editing quotes[editIdx].
  const [editIdx, setEditIdx] = useState<number | null>(null);

  function resetForm() {
    setCarrier('');
    setStatus('quoted');
    setPremiums({});
    setPayment('');
    setNote('');
    setFile(null);
    setEditIdx(null);
  }

  // Load an existing carrier's values into the form to edit it in place.
  function startEdit(i: number) {
    const q = quotes[i];
    if (!q) return;
    setCarrier(q.carrier ?? '');
    setStatus(q.status ?? 'quoted');
    const pm: Record<string, string> = {};
    (q.lobs ?? []).forEach((l) => {
      pm[l.lob] = String(l.premium ?? '');
    });
    setPremiums(pm);
    setPayment(q.payment ?? '');
    setNote(q.note ?? '');
    setFile(null);
    setEditIdx(i);
  }

  async function remove(i: number) {
    if (!window.confirm(`Remove ${quotes[i]?.carrier ?? 'this carrier'} from this submission?`)) return;
    await patch({ quotes: quotes.filter((_, j) => j !== i) });
    if (editIdx === i) resetForm();
    toast('Carrier removed');
  }

  // Add a new carrier, or save edits to the one currently being edited.
  async function save() {
    if (!carrier) {
      toast('Pick a carrier');
      return;
    }
    setBusy(true);
    try {
      const existing = editIdx !== null ? quotes[editIdx] : undefined;
      let storagePath = existing?.storagePath;
      let docName = existing?.docName;
      if (file) {
        const ext = (file.name.split('.').pop() || 'bin').toLowerCase().replace(/[^a-z0-9]/g, '');
        const path = `submissions/${id.replace(/[^A-Za-z0-9_-]/g, '')}/quotes/${crypto.randomUUID()}.${ext}`;
        const { error } = await supabase.storage
          .from('submission-docs')
          .upload(path, file, { contentType: file.type || undefined, upsert: false });
        if (error) throw error;
        storagePath = path;
        docName = file.name;
      }
      const lobs = lobKeys
        .map((k) => ({ lob: k, premium: (premiums[k] ?? '').trim() }))
        .filter((l) => l.premium !== '');
      const q: Quote = {
        carrier,
        status,
        note: note || undefined,
        payment: payment || undefined,
        storagePath,
        docName,
        lobs,
      };
      const nextQuotes = editIdx !== null ? quotes.map((x, i) => (i === editIdx ? q : x)) : [...quotes, q];
      const wasEdit = editIdx !== null;
      await patch({ quotes: nextQuotes });
      resetForm();
      toast(wasEdit ? 'Carrier updated' : 'Carrier added');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not save carrier');
    } finally {
      setBusy(false);
    }
  }

  async function build() {
    if (!quotes.some((q) => q.storagePath)) {
      toast('Add a carrier and upload its quote document first');
      return;
    }
    setBuilding(true);
    try {
      const { buildProposalPdf } = await import('../lib/buildProposal');
      const meta = await buildProposalPdf(id, data);
      const next: Partial<SubmissionData> = {
        proposalDoc: { path: meta.path, name: meta.name, at: Date.now() },
      };
      if (meta.totalPremium > 0) next.proposalPremium = String(meta.totalPremium);
      await patch(next);
      toast('Proposal built & saved — you can Send it to the agent now');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not build proposal');
    } finally {
      setBuilding(false);
    }
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <h3>Carriers &amp; quotes</h3>
      </div>
      <div className="panel-pad">
        {quotes.length === 0 && <div className="sub-meta" style={{ marginBottom: 12 }}>No carriers yet.</div>}
        {quotes.map((q, i) => (
          <div
            key={i}
            style={{
              padding: '8px 10px',
              margin: '0 -10px',
              borderBottom: '1px solid var(--border)',
              background: editIdx === i ? 'var(--slate-soft)' : undefined,
              borderRadius: editIdx === i ? 8 : undefined,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <b>{q.carrier}</b>
              <span className="tier-badge" style={{ marginTop: 0 }}>{CARRIER_STATUS_LABEL[q.status ?? 'quoted'] ?? q.status}</span>
            </div>
            {(q.lobs ?? []).map((l, j) => (
              <div key={j} className="sub-meta">
                {lobName(l.lob)}: {money(l.premium)}
              </div>
            ))}
            {q.payment && <div className="sub-meta">Payment: {q.payment}</div>}
            {q.docName && <div className="sub-meta">📎 {q.docName}</div>}
            {q.note && <div className="sub-meta">{q.note}</div>}
            <div style={{ display: 'flex', gap: 14, marginTop: 6 }}>
              <button className="reset-link" style={{ color: 'var(--navy)' }} onClick={() => startEdit(i)}>Edit</button>
              <button className="reset-link" onClick={() => void remove(i)}>Delete</button>
            </div>
          </div>
        ))}

        <div
          style={{ marginTop: 14, display: 'grid', gap: 8, ...(editIdx !== null ? { borderTop: '1px solid var(--border-strong)', paddingTop: 12 } : {}) }}
        >
          {editIdx !== null && (
            <div className="sub-meta" style={{ fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
              Editing {quotes[editIdx]?.carrier}
            </div>
          )}
          <select className="finput" value={carrier} onChange={(e) => setCarrier(e.target.value)}>
            <option value="">Add a carrier…</option>
            {carrier && !carrierNames.includes(carrier) && <option value={carrier}>{carrier}</option>}
            {carrierNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <select className="finput" value={status} onChange={(e) => setStatus(e.target.value)}>
            {Object.entries(CARRIER_STATUS_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="flabel">Premium by line</label>
            {lobKeys.map((k) => (
              <div key={k} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: 'var(--muted)', minWidth: 130 }}>{lobName(k)}</span>
                <input
                  className="finput"
                  placeholder="$0"
                  value={premiums[k] ?? ''}
                  onChange={(e) => setPremiums((p) => ({ ...p, [k]: currencyInput(e.target.value) }))}
                />
              </div>
            ))}
          </div>
          <textarea
            className="finput"
            rows={2}
            placeholder="Payment options (optional)"
            value={payment}
            onChange={(e) => setPayment(e.target.value)}
          />
          <input className="finput" placeholder="Note / decline reason (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer' }}>
            {file
              ? `📎 ${file.name}`
              : editIdx !== null && quotes[editIdx]?.docName
                ? `📎 ${quotes[editIdx]?.docName} — replace?`
                : 'Attach quote PDF'}
            <input type="file" hidden accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </label>
          {editIdx !== null ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={() => void save()} disabled={busy}>
                {busy ? 'Saving…' : 'Save changes'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={resetForm} disabled={busy}>
                Cancel
              </button>
            </div>
          ) : (
            <button className="btn btn-ghost btn-sm" onClick={() => void save()} disabled={busy}>
              {busy ? 'Adding…' : 'Add carrier'}
            </button>
          )}
        </div>

        {quotes.some((q) => q.storagePath) ? (
          <button
            className="btn btn-accent"
            style={{ marginTop: 14, width: '100%' }}
            onClick={() => void build()}
            disabled={building}
          >
            {building ? 'Building…' : 'Build Proposal'}
          </button>
        ) : (
          <div
            className="sub-meta"
            style={{ marginTop: 14, padding: '10px 12px', background: 'var(--slate-soft)', borderLeft: '3px solid var(--navy)', borderRadius: 8, lineHeight: 1.5 }}
          >
            The <b>Build Proposal</b> button appears here once you’ve added a carrier <b>and</b> uploaded its quote
            document above.
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Send proposal (emails the agent that their built proposal is ready) ----
export function ProposalPanel({ data, patch, id }: { data: SubmissionData; patch: Patch; id: string }) {
  const toast = useToast();
  const [note, setNote] = useState(data.proposalNote ?? '');
  const [busy, setBusy] = useState(false);
  const built = Boolean(data.proposalDoc?.path);

  async function send() {
    setBusy(true);
    try {
      await patch({ proposalNote: note }, 'proposal_sent');
      const r = await api.notifyProposalSent(id);
      toast(r.emailed ? 'Sent — agent emailed that their proposal is ready' : 'Marked proposal sent');
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not send');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <h3>Send proposal</h3>
      </div>
      <div className="panel-pad" style={{ display: 'grid', gap: 10 }}>
        {!built ? (
          <div className="sub-meta">Build a proposal above first — then you can send it to the agent.</div>
        ) : (
          <>
            <div className="sub-meta">
              Emails the agent that their proposal is ready and lets them download it from the portal with their PIN.
            </div>
            <input className="finput" placeholder="Note to agent (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
            <button className="btn btn-accent" onClick={() => void send()} disabled={busy}>
              {busy ? 'Sending…' : 'Send proposal to agent'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ---- Payment / bind ----
export function PaymentPanel({ data, patch }: { data: SubmissionData; patch: Patch }) {
  const toast = useToast();
  const [payNote, setPayNote] = useState(data.paymentNote ?? '');

  async function bind() {
    await patch({ paymentNote: payNote }, 'bound');
    toast('Marked bound');
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <h3>Payment &amp; bind</h3>
      </div>
      <div className="panel-pad" style={{ display: 'grid', gap: 10 }}>
        <div className="fgroup" style={{ margin: 0 }}>
          <label className="flabel">How payment is being handled</label>
          <textarea
            className="finput"
            rows={3}
            placeholder="e.g. Billed direct by carrier; agency bill; premium finance via ___; paid in full to AAS…"
            value={payNote}
            onChange={(e) => setPayNote(e.target.value)}
          />
        </div>
        <button className="btn btn-primary" onClick={() => void bind()}>
          Mark bound
        </button>
      </div>
    </div>
  );
}

// ---- Gatekeeper carriers ----
// Shown on a Gatekeeper-tier submission. Staff tick which required carrier(s) they're
// sending to (any mix of BITCO / EMC / FCCI, plus Liberty). "Prepare submission email(s)"
// drafts one email reviewed once and sent to each selected email carrier's underwriter;
// Liberty submits through its portal. Each carrier tracks its own progress.
// Appetite guides (agent-safe paths from the shared options) so staff can open the
// same guide the agent saw, right from the Gatekeeper panel when prepping the UW email.
const APPETITE_BY_KEY: Record<string, { doc?: string; note?: string }> = Object.fromEntries(
  GATEKEEPER_CARRIER_OPTIONS.map((o) => [o.key, { doc: o.appetiteDoc, note: o.appetiteNote }]),
);

export function GatekeeperPanel({
  data,
  patch,
  carriers,
  loading,
  onPrepare,
  title = 'Gatekeeper carriers',
  subtitle = "Select which carrier(s) you're sending to. We'll draft a submission email to each underwriter for you to review before sending.",
}: {
  data: SubmissionData;
  patch: Patch;
  carriers: GatekeeperCarrier[];
  loading?: boolean;
  onPrepare: (carriers: GatekeeperCarrier[]) => void;
  title?: string;
  subtitle?: string;
}) {
  const selected = Array.isArray(data.gatekeeperCarriers) ? data.gatekeeperCarriers : [];
  const pick = (key: string) => selected.find((c) => c.key === key);
  const appetiteAck = (data.appetiteAck as Record<string, boolean> | undefined) ?? {};

  async function toggle(key: string) {
    const next = pick(key)
      ? selected.filter((c) => c.key !== key)
      : [...selected, { key, status: 'pending', at: Date.now() }];
    await patch({ gatekeeperCarriers: next });
  }

  async function setStatus(key: string, status: string) {
    await patch({
      gatekeeperCarriers: selected.map((c) => (c.key === key ? { ...c, status, at: Date.now() } : c)),
    });
  }

  // Email carriers that are ticked — these go into the batch submission email.
  const emailSelected = carriers.filter((c) => c.mode === 'email' && pick(c.key));

  return (
    <div className="panel">
      <div className="panel-head">
        <h3>{title}</h3>
      </div>
      <div className="panel-pad">
        <p className="sub-meta" style={{ marginBottom: 10 }}>{subtitle}</p>
        {loading && carriers.length === 0 && <div className="sub-meta">Loading carriers…</div>}
        {carriers.map((c) => {
          const sel = pick(c.key);
          return (
            <div key={c.key} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
              <label style={{ display: 'flex', gap: 9, alignItems: 'center', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!sel} onChange={() => void toggle(c.key)} />
                <b style={{ color: 'var(--navy)' }}>{c.name}</b>
                {sel && sel.status !== 'pending' && (
                  <span className="tier-badge" style={{ marginTop: 0, marginLeft: 'auto' }}>
                    {GK_STATUS_LABEL[sel.status] ?? sel.status}
                  </span>
                )}
              </label>
              <div className="sub-meta" style={{ marginTop: 4, marginLeft: 27 }}>
                {c.mode === 'email' ? (
                  <>
                    {c.uwName} · {c.uwEmail}
                    {c.uwPhone ? ` · ${c.uwPhone}` : ''}
                  </>
                ) : (
                  c.note
                )}
              </div>
              {APPETITE_BY_KEY[c.key]?.doc && (
                <div
                  className="sub-meta"
                  style={{ marginTop: 4, marginLeft: 27, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}
                >
                  <a
                    href={APPETITE_BY_KEY[c.key]?.doc}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: 'var(--navy)', fontWeight: 600, textDecoration: 'underline' }}
                  >
                    Appetite guide ↗
                  </a>
                  {pick(c.key) &&
                    (appetiteAck[c.key] ? (
                      <span style={{ color: '#2E7D32', fontWeight: 600 }}>✓ Agent confirmed appetite fits</span>
                    ) : (
                      <span style={{ color: 'var(--muted)' }}>Agent didn't confirm appetite</span>
                    ))}
                </div>
              )}
              {sel && (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8, marginLeft: 27, flexWrap: 'wrap' }}>
                  {c.mode === 'portal' && (
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        if (c.portalUrl) window.open(c.portalUrl, '_blank', 'noopener');
                      }}
                    >
                      Open Liberty portal ↗
                    </button>
                  )}
                  <label className="sub-meta" style={{ display: 'flex', gap: 6, alignItems: 'center', margin: 0 }}>
                    Status
                    <select
                      className="finput"
                      style={{ width: 'auto', padding: '5px 9px', fontSize: 13 }}
                      value={sel.status}
                      onChange={(e) => void setStatus(c.key, e.target.value)}
                    >
                      {Object.entries(GK_STATUS_LABEL).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              )}
            </div>
          );
        })}
        {emailSelected.length > 0 && (
          <button
            className="btn btn-accent"
            style={{ marginTop: 14, width: '100%' }}
            onClick={() => onPrepare(emailSelected)}
          >
            Prepare submission email{emailSelected.length > 1 ? 's' : ''} ({emailSelected.length}) →
          </button>
        )}
      </div>
    </div>
  );
}

// ---- AI document extraction ----
export function ExtractPanel({ data, patch }: { data: SubmissionData; patch: Patch }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const extracted = (data.extracted ?? {}) as Record<string, unknown>;

  const readable = (data.documents ?? []).filter((d) => d.storagePath && docMime(d.fileName));

  async function runExtract(payload: { mime: string; data: string; name?: string }[]) {
    if (!payload.length) {
      toast('No readable documents (PDF or image) to read');
      return;
    }
    setBusy(true);
    try {
      const { extracted: obj } = await api.aiExtract(payload);
      await patch({ extracted: { ...extracted, ...(obj as Record<string, unknown>) } });
      toast('Details extracted');
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Extraction failed');
    } finally {
      setBusy(false);
    }
  }

  async function readUploaded() {
    setBusy(true);
    const payload: { mime: string; data: string; name?: string }[] = [];
    try {
      for (const d of readable) {
        const mime = docMime(d.fileName);
        if (!d.storagePath || !mime) continue;
        const { data: blob, error } = await supabase.storage.from('submission-docs').download(d.storagePath);
        if (error || !blob) continue;
        payload.push({ mime, data: toBase64(await blob.arrayBuffer()), name: d.fileName || d.kind });
      }
    } catch {
      /* fall through — runExtract reports if empty */
    }
    setBusy(false);
    await runExtract(payload);
  }

  async function onFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length === 0) return;
    const payload = await Promise.all(
      files.map(async (f) => ({ mime: f.type, data: toBase64(await f.arrayBuffer()), name: f.name })),
    );
    await runExtract(payload);
  }

  const rows = EXTRACT_FIELDS.filter(([k]) => extracted[k]);

  return (
    <div className="panel">
      <div className="panel-head">
        <h3>AI document read</h3>
      </div>
      <div className="panel-pad">
        <p className="sub-meta" style={{ marginBottom: 10 }}>
          Reads the ACORDs, dec pages, and loss runs uploaded to this submission to auto-fill the key fields.
        </p>
        <button className="btn btn-ghost btn-sm" onClick={() => void readUploaded()} disabled={busy || readable.length === 0}>
          <SparkIcon width={15} height={15} /> {busy ? 'Reading…' : `Read uploaded documents${readable.length ? ` (${readable.length})` : ''}`}
        </button>
        {readable.length === 0 && (
          <div className="sub-meta" style={{ marginTop: 8 }}>No PDF/image documents uploaded to this submission yet.</div>
        )}
        <label className="reset-link" style={{ cursor: 'pointer', fontSize: 12, display: 'inline-block', marginTop: 10 }}>
          or read a file from your computer
          <input type="file" hidden multiple accept="application/pdf,image/*" onChange={(e) => void onFiles(e)} disabled={busy} />
        </label>

        {rows.length > 0 && (
          <div style={{ marginTop: 12 }}>
            {rows.map(([k, label]) => (
              <div
                key={k}
                style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--border)' }}
              >
                <span className="sub-meta">{label}</span>
                <button
                  className="reset-link"
                  title="Copy"
                  onClick={() => {
                    void navigator.clipboard.writeText(String(extracted[k]));
                    toast('Copied');
                  }}
                >
                  {String(extracted[k])}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
